import React, { useCallback } from 'react';
import { FieldLabel } from '@puckeditor/core';
import type { CustomField } from '@puckeditor/core';

import {
  FIELD_CONTROL_BG,
  FIELD_CONTROL_BORDER_COLOR,
  FIELD_CONTROL_RADIUS,
  selectControlStyle,
  disabledSelectControlStyle,
} from './field-control-style';

type BooleanFieldProps = {
  field: { label?: string };
  name: string;
  value: boolean;
  onChange: (value: boolean) => void;
  readOnly?: boolean;
};

const BOOLEAN_OPTIONS = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
] as const;

const hintStyle: React.CSSProperties = {
  marginTop: 4,
  fontSize: 12,
  color: 'var(--dcm-cms-field-hint-color, #646d72)',
};

/** A labelled select that persists booleans rather than the DOM's string option values. */
function BooleanField(hint?: string) {
  return function BooleanFieldWidget({ field, name, value, onChange, readOnly }: BooleanFieldProps): JSX.Element {
    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLSelectElement>) => onChange(event.currentTarget.value === 'true'),
      [onChange]
    );
    const hintId = hint ? `boolean-select-hint-${name}` : undefined;
    const selectedValue = typeof value === 'boolean' ? String(value) : '';

    return (
      <FieldLabel label={field.label ?? name} readOnly={readOnly}>
        <select
          value={selectedValue}
          onChange={handleChange}
          disabled={readOnly}
          data-testid={`boolean-select-${name}`}
          aria-describedby={hintId}
          style={{
            ...selectControlStyle,
            borderColor: `var(--dcm-cms-field-border-color, ${FIELD_CONTROL_BORDER_COLOR})`,
            borderRadius: `var(--dcm-cms-field-radius, ${FIELD_CONTROL_RADIUS})`,
            backgroundColor: `var(--dcm-cms-field-background, ${FIELD_CONTROL_BG})`,
            ...(readOnly && disabledSelectControlStyle),
          }}
        >
          {selectedValue === '' && <option value=''>Select…</option>}
          {BOOLEAN_OPTIONS.map((option) => (
            <option key={String(option.value)} value={String(option.value)}>
              {option.label}
            </option>
          ))}
        </select>
        {hint && (
          <p id={hintId} data-testid={`boolean-select-hint-${name}`} style={hintStyle}>
            {hint}
          </p>
        )}
      </FieldLabel>
    );
  };
}

/** Builds a Puck field whose UI and AI schema both use actual JSON booleans. */
export function booleanField(label: string, aiInstructions?: string, hint?: string): CustomField<boolean> {
  return {
    type: 'custom',
    label,
    render: BooleanField(hint),
    ai: {
      schema: { type: 'boolean' },
      ...(aiInstructions ? { instructions: aiInstructions } : {}),
    },
  };
}
